import React, { useState } from "react";
import { 
  TextField, 
  IconButton, 
  Box, 
  Tooltip,
  CircularProgress 
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useToast } from "../../Hooks/useToast";
import { FetchEditReservations } from "../../Apis/Reservations/Reservations";

const EditableEmailCell = ({ 
  value, 
  reservationId, 
  onEmailUpdate,
  disabled = false 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [emailValue, setEmailValue] = useState(value || "");
  const [isLoading, setIsLoading] = useState(false);
  const { toastSuccess, toastError } = useToast();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setEmailValue(value || "");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEmailValue(value || "");
  };

  const handleSave = async () => {
    if (!validateEmail(emailValue)) {
      toastError("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }

    if (emailValue === value) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await FetchEditReservations(
        { customerEmail: emailValue },
        reservationId
      );

      if (response.status === 200 || response.status === 201) {
        toastSuccess("E-Mail erfolgreich aktualisiert!");
        setIsEditing(false);
        if (onEmailUpdate) {
          onEmailUpdate(reservationId, emailValue);
        }
      } else {
        toastError(`Fehler beim Aktualisieren: ${response.data?.message || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error("Email update error:", error);
      toastError("Fehler beim Aktualisieren der E-Mail. Bitte erneut versuchen.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSave();
    } else if (event.key === "Escape") {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        gap: 1,
        width: "100%",
        minWidth: 200
      }}>
        <TextField
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
          onKeyDown={handleKeyPress}
          size="small"
          variant="outlined"
          placeholder="E-Mail eingeben"
          autoFocus
          disabled={isLoading}
          error={emailValue && !validateEmail(emailValue)}
          helperText={emailValue && !validateEmail(emailValue) ? "Ungültige E-Mail" : ""}
          sx={{ 
            flex: 1,
            "& .MuiOutlinedInput-root": {
              fontSize: "14px"
            }
          }}
        />
        <Tooltip title="Speichern">
          <IconButton
            onClick={handleSave}
            disabled={isLoading || !validateEmail(emailValue)}
            size="small"
            color="primary"
          >
            {isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <SaveIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Abbrechen">
          <IconButton
            onClick={handleCancel}
            disabled={isLoading}
            size="small"
            color="secondary"
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: "flex", 
      alignItems: "center", 
      gap: 1,
      width: "100%",
      minHeight: 40
    }}>
      <Box sx={{ 
        flex: 1,
        color: !value || value === "" ? "#999" : "#333",
        fontStyle: !value || value === "" ? "italic" : "normal"
      }}>
        {value || "Keine E-Mail"}
      </Box>
      {!disabled && (
        <Tooltip title="E-Mail bearbeiten">
          <IconButton
            onClick={handleEdit}
            size="small"
            sx={{ 
              opacity: 0.7,
              "&:hover": { opacity: 1 }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default EditableEmailCell;